// Bulk Enquiry (/bulk-enquiry): the company's uploaded batches. A batch opens
// at /bulk-enquiry/:batchId (BatchDetail).
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { debounce } from 'lodash'
import { PackagePlus, SearchLg } from '@untitled-ui/icons-react'
import { MyButton, MyColumn, MyDataTable, MyHorizontalTabV2, MyTextField } from '@interstellar-component'
import TruncatedText from '@src/components/TruncatedText'
import { useApp } from '@src/AppContext'
import { Access, formatDateTime } from '@src/services/Helper'
import paginatorToMeta from '@src/services/pagination'
import Service from './service'
import usePagedList from './usePagedList'
import StatusChip from './components/StatusChip'
import LoadError from './components/LoadError'
import NewBulkEnquirySlider from './components/NewBulkEnquirySlider'
import FundingChips from './components/FundingChips'
import {
  BATCH_STATUS,
  CREATE_PERMISSION,
  ROW_STATUS,
  SEARCH_MAX_LENGTH,
  batchRowTotal,
  chipOf,
} from './constants'

const PAGE_SIZE = 10
const STATUS_TABS = [
  { value: 'all', label: 'All' },
  ...Object.entries(BATCH_STATUS).map(([value, { label }]) => ({ value, label })),
]

function BulkEnquiry() {
  const navigate = useNavigate()
  const location = useLocation()
  const { hasPermission } = useApp()
  const canUpload = hasPermission(Access.ENQUIRY, CREATE_PERMISSION)

  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('all')
  // A batch page's "Upload a new batch" lands here with { openUpload, companyId }:
  // the slider opens for that company.
  const [upload, setUpload] = useState(() =>
    location.state?.openUpload ? { open: true, companyId: location.state.companyId || null } : { open: false }
  )
  const showUpload = upload.open

  // That request arrives once: dropped from the history entry, so a reload
  // does not open the slider again.
  useEffect(() => {
    if (location.state?.openUpload) navigate(location.pathname, { replace: true, state: null })
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const fetchBatches = useCallback(
    () =>
      Service.list({
        page,
        limit: PAGE_SIZE,
        search: search || undefined,
        status: status === 'all' ? undefined : status,
      }),
    [page, search, status]
  )
  const batches = usePagedList(fetchBatches)

  const debouncedSearch = useMemo(
    () =>
      debounce((e) => {
        setSearch(e.target.value.trim())
        setPage(1)
      }, 500),
    []
  )
  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch])

  const batchCount = batches.paginator?.total ?? 0
  const values = {
    data: batches.data,
    loading: batches.loading,
    meta: paginatorToMeta(batches.paginator),
    checkedAll: false,
  }

  return (
    <div className="flex h-full w-full flex-col bg-gray-50/50">
      <div className="flex w-full flex-1 min-h-0 flex-col">
        <div className="flex flex-col gap-4 px-8 pt-8 pb-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex flex-col gap-1">
            <h1 className="text-2xl font-semibold text-gray-900">Bulk Enquiry</h1>
            <p className="text-sm text-gray-500">
              Upload many enquiries at once, check every row, then complete the files each row needs.
            </p>
          </div>
          {canUpload && (
            <MyButton
              type="button"
              color="primary"
              variant="filled"
              size="md"
              customClassname="gap-2 w-max"
              onClick={() => setUpload({ open: true, companyId: null })}
            >
              <PackagePlus className="size-5" stroke="currentColor" /> New bulk upload
            </MyButton>
          )}
        </div>

        <div className="flex flex-1 min-h-0 flex-col px-8 pb-8">
          <div className="flex flex-1 min-h-0 flex-col overflow-hidden rounded-xl border border-gray/200 bg-white shadow-sm">
            <div className="flex items-center gap-3 border-b border-gray/200 bg-gray/25 px-6 py-3">
              <h3 className="text-[14px] font-semibold text-gray-900">Uploaded batches</h3>
              <span className="rounded-full border border-gray-blue/200 bg-gray-blue/50 px-2 py-0.5 text-xs font-medium text-gray-blue/700">
                {batchCount} {batchCount === 1 ? 'batch' : 'batches'}
              </span>
            </div>

            <div className="flex flex-col gap-4 border-b border-gray-200 p-5 xl:flex-row xl:items-center xl:justify-between">
              <div className="w-full max-w-sm">
                <MyTextField
                  id="input-search-bulk-batch"
                  placeholder="Search batch no. or file name"
                  maxLength={SEARCH_MAX_LENGTH}
                  startAdornment={<SearchLg className="size-5 text-gray-light/600" stroke="currentColor" />}
                  focusColor="#14378E"
                  onChangeForm={debouncedSearch}
                />
              </div>
              <MyHorizontalTabV2
                value={status}
                fitContent
                onChange={(value) => {
                  setStatus(value)
                  setPage(1)
                }}
                tabs={STATUS_TABS}
              />
            </div>

            <div className="flex flex-1 min-h-0 flex-col">
              {batches.error ? (
                <LoadError
                  title="Could not load the batches"
                  message={batches.error.message}
                  onRetry={batches.reload}
                />
              ) : (
                <MyDataTable
                  values={values}
                  onClick={(row) => navigate(`/bulk-enquiry/${row.id}`)}
                  cursorPointer
                  paginator
                  onChangePagination={setPage}
                >
                  <MyColumn
                    header="Batch"
                    field="batch_no"
                    body={(row) => (
                      <div className="flex max-w-[240px] min-w-0 flex-col gap-0.5 py-1">
                        {/* A real link too: keyboard, new tab, copy link. */}
                        <Link
                          to={`/bulk-enquiry/${row.id}`}
                          onClick={(e) => e.stopPropagation()}
                          className="w-max text-sm font-semibold text-brand/700 hover:underline"
                        >
                          {row.batch_no}
                        </Link>
                        <TruncatedText text={row.file_name || '—'} className="text-xs text-gray-500" />
                      </div>
                    )}
                  />
                  <MyColumn
                    header="Company"
                    field="company"
                    body={(row) => (
                      <TruncatedText text={row.company?.name || '—'} className="text-sm text-gray-900" />
                    )}
                  />
                  <MyColumn
                    header="Uploaded"
                    field="created_at"
                    body={(row) => (
                      <div className="flex flex-col gap-0.5 py-1">
                        <span className="text-sm text-gray-900">{formatDateTime(row.created_at)}</span>
                        <span className="text-xs text-gray-500">{row.created_by?.name || '—'}</span>
                      </div>
                    )}
                  />
                  <MyColumn
                    header="Rows"
                    field="counts"
                    alignment="center"
                    body={(row) => <span className="text-sm font-medium text-gray-900">{batchRowTotal(row)}</span>}
                  />
                  <MyColumn
                    header="Row status"
                    field="row_status"
                    body={(row) => (
                      <div className="flex flex-wrap items-center gap-1.5 py-1">
                        {Object.entries(ROW_STATUS)
                          .filter(([key]) => row.counts?.[key] > 0)
                          .map(([key, { label, color }]) => (
                            <StatusChip key={key} label={`${row.counts[key]} ${label}`} color={color} />
                          ))}
                      </div>
                    )}
                  />
                  <MyColumn
                    header="Funding"
                    field="funding"
                    body={(row) => (
                      <div className="py-1">
                        <FundingChips
                          funding={row.funding}
                          empty={<span className="text-sm text-gray-400">—</span>}
                        />
                      </div>
                    )}
                  />
                  <MyColumn
                    header="Status"
                    field="status"
                    body={(row) => <StatusChip {...chipOf(BATCH_STATUS, row.status)} dot />}
                  />
                </MyDataTable>
              )}
            </div>
          </div>
        </div>
      </div>

      {canUpload && (
        <NewBulkEnquirySlider
          open={showUpload}
          companyId={upload.companyId || null}
          onClose={() => setUpload({ open: false })}
        />
      )}
    </div>
  )
}

export default BulkEnquiry
